'use client'

import React, { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import { Button } from "../components/ui/button";
import { Loader2 } from 'lucide-react';
import { Flex, Text } from "@radix-ui/themes";
import { Input } from "../components/ui/input";
import { cn, getBaseUrl } from "@/lib/utils";
import { StorageService } from "../services/storageService";

interface Props {
  onNext: () => void;
  allowLaterSubmission: boolean;
}

const CheckoutForm = ({
  onNext,
  allowLaterSubmission
}: Props) => {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [nameOnCard, setNameOnCard] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Stripe.js hasn't yet loaded.
    // Make sure to disable form submission until Stripe.js has loaded.
    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: getBaseUrl(),
        payment_method_data: {
          billing_details: {
            name: nameOnCard
          },
        }
      },
      redirect: "if_required"
    });

    if (setupIntent) {
      // Success! Associate customer id to user...
      await fetch('/api/v1/verify/payment/attachPaymentMethod', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          setupIntent: setupIntent.id,
          paymentMethod: setupIntent.payment_method
        }),
      })

      await fetch('/api/v1/verify/payment/save', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          setupIntent: setupIntent.id,
        }),
      })

      StorageService.clearPaymentCache();

      if (onNext) onNext();
    }

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error?.type === "card_error") {
      setMessage(error?.message ?? "");
    } else if (error?.type === "validation_error") {
      setMessage("");
    } else if (error) {
      setMessage("An unexpected error occurred.");
    }

    setIsLoading(false);
  };

  const SubmitButton = () => (
    <Button
      disabled={isLoading || !stripe || !elements}
      onClick={handleSubmit}
      className="h-10 w-full mt-8"
    >
      {isLoading &&
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      }
      Submit
    </Button>
  )

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="w-full">
      <Flex direction="column" className="mb-3">
        <label className="text-sm text-gray-700 mb-0.5">Name on card</label>
        <Input
          onBlur={() => {
            if (nameOnCard === "") {
              setNameTouched(true);
            }
          }}
          placeholder="Jane Doe"
          value={nameOnCard}
          onChange={(e) => {
            setNameOnCard(e.target.value)
          }}
          className={cn(
            "h-[42px] text-base text-[#30313d] rounded-sm systemFontFamily",
            "focus-visible:ring-2 focus-visible:ring-[#0573E1]/50 transition-colors",
            (nameTouched && nameOnCard === "") ? "ring-2 ring-[#df1b41]" : ""
          )}
        />
        {nameTouched && nameOnCard === "" &&
          <Text className="text-[#df1b41] text-sm pt-1">Please supply a name.</Text>
        }
      </Flex>
      <PaymentElement id="payment-element" />
      {message && <Flex id="payment-message" className="text-sm text-red-500 mt-3">
        {message}
      </Flex>}
      <SubmitButton />
      {
        allowLaterSubmission ? (
          <Flex justify="center" className="mt-2">
            <Text className="text-slate-900 cursor-pointer text-sm font-medium" onClick={onNext}>
              I&apos;ll do this later
            </Text>
          </Flex>
        ) : null
      }
    </form>
  );
}

export default CheckoutForm;
